import { Injectable } from '@angular/core';
import {
  Excursion,
  ExcursionConfigurationKeys,
  ExcursionDataType,
  ExcursionKeys,
  ExcursionOptionKey,
  ExcursionOptionsType,
  ExcursionPreferenceKeys,
  ExcursionType,
} from 'ag-common-lib/lib/models/registration/excursion.model';
import {
  ModalWindowActionPayload,
  ModalWindowUserAction,
} from 'ag-common-svc/lib/components/modal-window/modal-window.model';
import { ModalWindowService } from 'ag-common-svc/lib/components/modal-window/modal-window.service';
import { BaseFormService, ConferenceService } from 'ag-common-svc/public-api';
import { cloneDeep, get, set, uniqueId } from 'lodash';
import { filter, firstValueFrom, switchMap } from 'rxjs';
import { ExcursionModalAction, ExcursionModalPayload } from './excursion-modal.model';
import { BaseModelKeys } from 'ag-common-lib/lib/models/base.model';
import { validateDxGroups } from 'ag-common-svc/lib/utils/validation';
import { format, isSameDay } from 'date-fns';
import { Constants } from '@ag-common-lib/public-api';
import { ToastrService } from 'ngx-toastr';

@Injectable()
export class ExcursionModalService extends BaseFormService<Excursion> {
  readonly excursionModalPopupId = uniqueId('excursionModalPopupId');
  readonly validationGroup = uniqueId('excursionValidationGroup');
  readonly excursionDescriptionValidationGroup = uniqueId('excursionDescriptionValidationGroup');

  constructor(
    private toastrService: ToastrService,
    private conferenceService: ConferenceService,
    private modalWindowService: ModalWindowService,
  ) {
    super();
  }

  showModal = async (payload: ExcursionModalPayload) => {
    const excursion = payload?.excursion;
    const formData = this.getFormData(excursion);

    await firstValueFrom(
      this.modalWindowService.prompt(this.excursionModalPopupId).pipe(
        switchMap(async (actionPayload: ModalWindowActionPayload) => {
          const actionType = actionPayload?.type;

          if (!actionType || actionType === ModalWindowUserAction.abort) {
            throw Error('abort');
          }

          this.startProgress();

          const isValid = await validateDxGroups([this.validationGroup, this.excursionDescriptionValidationGroup]);
          const isUniq = this.validateIsExcursionUniq(payload);
          if (!isUniq) {
            this.toastrService.error(
              `The "${formData?.[ExcursionKeys.name]}" excursion already exists on ${format(
                formData?.[ExcursionKeys.date],
                Constants.DISPLAY_DATE_FORMAT,
              )}!`,
            );
            return false;
          }

          if (!isValid || !isUniq) {
            this.stopProgress();
            return false;
          }

          await this.handleSaveExcursion(payload);

          this.formChangesDetector.clear();

          actionPayload.modalWindow?.forceCloseModal();

          return true;
        }),
        filter(Boolean),
      ),
    );
    this.stopProgress();
  };

  private handleSaveExcursion = async (payload: ExcursionModalPayload) => {
    const action = payload?.action;
    const conference = payload?.conference;
    const conferenceDbId = conference?.[BaseModelKeys.dbId];
    const path = payload?.path;
    const excursions = get(conference, path);
    const formData = this.formDataSig();
    const activities = [];

    if (new Set([ExcursionModalAction.copy, ExcursionModalAction.create]).has(action)) {
      activities.push(formData);

      excursions?.forEach(excursion => {
        const activity = cloneDeep(excursion);

        activities.push(activity);
      });
    }

    if (new Set([ExcursionModalAction.edit]).has(action)) {
      excursions?.forEach(excursion => {
        const activity = cloneDeep(excursion);
        if (formData?.[ExcursionKeys.id] === excursion?.[ExcursionKeys.id]) {
          Object.assign(activity, formData);
        }
        activities.push(activity);
      });
    }

    if (!conferenceDbId) {
      set(conference, path, activities);
      return false;
    }
    const updates = {};
    set(updates, path, activities);

    return this.conferenceService.updateFields(conferenceDbId, updates).then(() => {
      excursions.length = 0;
      excursions.push(...activities);

      return false;
    });
  };

  private getFormData = (excursion: Excursion) => {
    const initialData = Object.assign({}, new Excursion(), excursion);
    const formData = new Proxy(initialData, {
      set: (target, prop, value, receiver) => {
        const prevValue = target[prop];
        this.formChangesDetector.handleChange(prop, value, prevValue);
        Reflect.set(target, prop, value, receiver);

        switch (prop) {
          case ExcursionKeys.type:
            this.setPreferences(value);
            this.setConfigurations(value);
            break;
        }

        this.formDataSig.update(data => data);

        return true;
      },
    });

    this.formDataSig.set(formData);

    return formData;
  };

  private validateIsExcursionUniq = (payload: ExcursionModalPayload) => {
    const action = payload?.action;
    const conference = payload?.conference;
    const path = payload?.path;
    const excursions = get(conference, path);
    const formData = this.formDataSig();

    if (new Set([ExcursionModalAction.copy, ExcursionModalAction.create]).has(action)) {
      return excursions?.every(excursion => !this.checkIsEqualExcursion(excursion, formData));
    }

    if (new Set([ExcursionModalAction.edit]).has(action)) {
      return excursions?.every(
        excursion =>
          formData?.[ExcursionKeys.id] === excursion?.[ExcursionKeys.id] ||
          !this.checkIsEqualExcursion(excursion, formData),
      );
    }

    return false;
  };

  private checkIsEqualExcursion = (left: Excursion, right: Excursion) => {
    return (
      left?.[ExcursionKeys.name]?.trim()?.toLocaleLowerCase() ===
        right?.[ExcursionKeys.name]?.trim()?.toLocaleLowerCase() &&
      isSameDay(left?.[ExcursionKeys.date], right?.[ExcursionKeys.date])
    );
  };

  private setConfigurations = (value: ExcursionType) => {
    this.formDataSig()[ExcursionKeys.configuration] = [];

    switch (value) {
      case ExcursionType.golf:
        this.setDefaultGolfConfigurations();
        return;
      case ExcursionType.spa:
        this.setDefaultSpaConfigurations();
        return;
    }
  };

  private setPreferences = (value: ExcursionType) => {
    this.formDataSig()[ExcursionKeys.preferences] = [];

    switch (value) {
      case ExcursionType.golf:
        this.setDefaultGolfPreferences();
        return;
      case ExcursionType.spa:
        this.setDefaultSpaPreferences();
        return;
      case ExcursionType.genTime:
        this.setDefaultGenTimePreferences();
        return;
    }
  };

  private setDefaultGolfPreferences = () => {
    this.formDataSig()[ExcursionKeys.preferences] = [
      {
        [ExcursionPreferenceKeys.label]: 'Clubs',
        [ExcursionPreferenceKeys.selectionType]: ExcursionOptionsType.single,
        [ExcursionPreferenceKeys.instructions]: 'Provide information about clubs',
        [ExcursionPreferenceKeys.items]: [
          {
            [ExcursionOptionKey.id]: 'Right Handed Clubs',
            [ExcursionOptionKey.name]: 'Right Handed Clubs',
            [ExcursionOptionKey.cost]: 50,
          },
          {
            [ExcursionOptionKey.id]: 'Left Handed Clubs',
            [ExcursionOptionKey.name]: 'Left Handed Clubs',
            [ExcursionOptionKey.cost]: 50,
          },
          {
            [ExcursionOptionKey.id]: 'No Clubs',
            [ExcursionOptionKey.name]: 'No Clubs',
            [ExcursionOptionKey.cost]: null,
          },
        ],
      },
    ];
  };

  private setDefaultSpaPreferences = () => {
    this.formDataSig()[ExcursionKeys.preferences] = [
      {
        [ExcursionPreferenceKeys.label]: 'Appointment',
        [ExcursionPreferenceKeys.selectionType]: ExcursionOptionsType.single,
        [ExcursionPreferenceKeys.instructions]: 'Provide information about preferences for a daytime and a therapist',
        [ExcursionPreferenceKeys.items]: [
          {
            [ExcursionOptionKey.id]: 'Morning',
            [ExcursionOptionKey.name]: 'Morning',
            [ExcursionOptionKey.cost]: null,
          },
          {
            [ExcursionOptionKey.id]: 'Afternoon',
            [ExcursionOptionKey.name]: 'Afternoon',
            [ExcursionOptionKey.cost]: null,
          },
        ],
      },
      {
        [ExcursionPreferenceKeys.label]: 'Preferences for a therapist',
        [ExcursionPreferenceKeys.selectionType]: ExcursionOptionsType.single,
        [ExcursionPreferenceKeys.instructions]: null,
        [ExcursionPreferenceKeys.items]: [
          {
            [ExcursionOptionKey.id]: 'Males',
            [ExcursionOptionKey.name]: 'Male',
            [ExcursionOptionKey.cost]: null,
          },
          {
            [ExcursionOptionKey.id]: 'Female',
            [ExcursionOptionKey.name]: 'Female',
            [ExcursionOptionKey.cost]: null,
          },
          {
            [ExcursionOptionKey.id]: 'No Preference',
            [ExcursionOptionKey.name]: 'No Preference',
            [ExcursionOptionKey.cost]: null,
          },
        ],
      },
    ];
  };

  private setDefaultGenTimePreferences = () => {
    this.formDataSig()[ExcursionKeys.preferences] = [
      {
        [ExcursionPreferenceKeys.label]: 'Preferences for a therapist',
        [ExcursionPreferenceKeys.selectionType]: ExcursionOptionsType.single,
        [ExcursionPreferenceKeys.instructions]: 'Provide information about preferences for a male or female specialist',
        [ExcursionPreferenceKeys.items]: [
          {
            [ExcursionOptionKey.id]: 'Males',
            [ExcursionOptionKey.name]: 'Male',
            [ExcursionOptionKey.cost]: null,
          },
          {
            [ExcursionOptionKey.id]: 'Females',
            [ExcursionOptionKey.name]: 'Female',
            [ExcursionOptionKey.cost]: null,
          },
          {
            [ExcursionOptionKey.id]: 'No Preference',
            [ExcursionOptionKey.name]: 'No Preference',
            [ExcursionOptionKey.cost]: null,
          },
        ],
      },
    ];
  };

  private setDefaultSpaConfigurations = () => {
    this.formDataSig()[ExcursionKeys.configuration] = [
      {
        [ExcursionConfigurationKeys.label]: 'SPA Appointment Time',
        [ExcursionConfigurationKeys.instructions]: 'Provide SPA Appointment Time',
        [ExcursionConfigurationKeys.dataType]: ExcursionDataType.time,
      },
      {
        [ExcursionConfigurationKeys.label]: 'SPA Confirmation Number',
        [ExcursionConfigurationKeys.instructions]: 'Provide SPA Confirmation Number',
        [ExcursionConfigurationKeys.dataType]: ExcursionDataType.string,
      },
    ];
  };

  private setDefaultGolfConfigurations() {
    this.formDataSig()[ExcursionKeys.configuration] = [
      {
        [ExcursionConfigurationKeys.label]: 'Golf Or Pickleball Team Name',
        [ExcursionConfigurationKeys.instructions]: 'Provide information about Golf Or Pickleball teams',
        [ExcursionConfigurationKeys.dataType]: ExcursionDataType.string,
      },
    ];
  }
}
